import { z } from "zod";

// ---------------------------------------------------------------------------
// Enums (string values match the Prisma enums in packages/db)
// ---------------------------------------------------------------------------

export const UserRole = z.enum(["analyst", "admin"]);
export type UserRole = z.infer<typeof UserRole>;

export const Timeframe = z.enum(["1d", "1h", "15m"]);
export type Timeframe = z.infer<typeof Timeframe>;

export const PickDirection = z.enum(["long", "short"]);
export type PickDirection = z.infer<typeof PickDirection>;

export const PickStatus = z.enum(["draft", "published", "closed"]);
export type PickStatus = z.infer<typeof PickStatus>;

export const PickVisibility = z.enum(["private", "internal", "public"]);
export type PickVisibility = z.infer<typeof PickVisibility>;

export const PickClosedReason = z.enum([
  "target_hit",
  "stop_hit",
  "thesis_invalidated",
  "time_expiry",
  "manual",
]);
export type PickClosedReason = z.infer<typeof PickClosedReason>;

export const JobRunStatus = z.enum(["queued", "running", "succeeded", "failed"]);
export type JobRunStatus = z.infer<typeof JobRunStatus>;

// ---------------------------------------------------------------------------
// Primitives
// ---------------------------------------------------------------------------

export const Uuid = z.string().uuid();

// Decimals cross the wire as strings so no precision is lost in JSON.
export const DecimalString = z.string().regex(/^-?\d{1,12}(\.\d{1,6})?$/, "invalid decimal");

export const IsoDateTime = z.string().datetime({ offset: true });

export const IdParams = z.object({ id: Uuid }).strict();
export type IdParams = z.infer<typeof IdParams>;

export const MAX_PAGE_SIZE = 100;

export const PageQuery = z
  .object({
    cursor: Uuid.optional(),
    take: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).default(50),
  })
  .strict();
export type PageQuery = z.infer<typeof PageQuery>;

export const pageOf = <T extends z.ZodTypeAny>(item: T) =>
  z.object({ items: z.array(item), nextCursor: Uuid.nullable() });

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

export const SessionUser = z.object({
  id: Uuid,
  tenantId: Uuid,
  email: z.string().email(),
  name: z.string(),
  role: UserRole,
});
export type SessionUser = z.infer<typeof SessionUser>;

export const LoginBody = z
  .object({ email: z.string().trim().toLowerCase().email().max(254), password: z.string().min(1).max(256) })
  .strict();
export type LoginBody = z.infer<typeof LoginBody>;

// ---------------------------------------------------------------------------
// Instruments and bars
// ---------------------------------------------------------------------------

export const Instrument = z.object({
  id: Uuid,
  ticker: z.string(),
  exchange: z.string(),
  name: z.string(),
  sector: z.string().nullable(),
  active: z.boolean(),
});
export type Instrument = z.infer<typeof Instrument>;

export const InstrumentListQuery = PageQuery.extend({
  q: z.string().trim().min(1).max(40).optional(),
  sector: z.string().trim().min(1).max(80).optional(),
  active: z.enum(["true", "false"]).transform((v) => v === "true").optional(),
}).strict();
export type InstrumentListQuery = z.infer<typeof InstrumentListQuery>;

export const Bar = z.object({
  ts: IsoDateTime,
  open: DecimalString,
  high: DecimalString,
  low: DecimalString,
  close: DecimalString,
  /** Split and dividend adjusted close from the vendor; null when none was stored. Open, high and low are raw. */
  adjClose: DecimalString.nullable(),
  volume: z.string().regex(/^\d+$/),
});
export type Bar = z.infer<typeof Bar>;

// Hard cap on rows per bars request. The chart asks for the newest N bars, never the full history.
export const MAX_BARS = 2000;

export const BarsQuery = z
  .object({
    timeframe: Timeframe.default("1d"),
    from: IsoDateTime.optional(),
    to: IsoDateTime.optional(),
    limit: z.coerce.number().int().min(1).max(MAX_BARS).default(500),
  })
  .strict();
export type BarsQuery = z.infer<typeof BarsQuery>;

// ---------------------------------------------------------------------------
// Watchlists
// ---------------------------------------------------------------------------

export const Watchlist = z.object({
  id: Uuid,
  name: z.string(),
  ownerId: Uuid,
  itemCount: z.number().int().nonnegative(),
  createdAt: IsoDateTime,
});
export type Watchlist = z.infer<typeof Watchlist>;

export const WatchlistItem = z.object({
  id: Uuid,
  instrumentId: Uuid,
  ticker: z.string(),
  position: z.number().int(),
  note: z.string().nullable(),
});
export type WatchlistItem = z.infer<typeof WatchlistItem>;

export const WatchlistDetail = Watchlist.extend({ items: z.array(WatchlistItem) });
export type WatchlistDetail = z.infer<typeof WatchlistDetail>;

// ---------------------------------------------------------------------------
// Picks
// ---------------------------------------------------------------------------

export const Pick = z.object({
  id: Uuid,
  authorId: Uuid,
  authorName: z.string(),
  instrumentId: Uuid,
  ticker: z.string(),
  direction: PickDirection,
  thesis: z.string(),
  entry: DecimalString,
  target: DecimalString,
  stop: DecimalString,
  currency: z.string().length(3),
  riskPerTradePct: DecimalString,
  status: PickStatus,
  visibility: PickVisibility,
  publishedAt: IsoDateTime.nullable(),
  closedAt: IsoDateTime.nullable(),
  outcomePct: DecimalString.nullable(),
  closedReason: PickClosedReason.nullable(),
  version: z.number().int().positive(),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type Pick = z.infer<typeof Pick>;

export const PickListQuery = PageQuery.extend({
  status: PickStatus.optional(),
  visibility: PickVisibility.optional(),
  instrumentId: Uuid.optional(),
  mine: z.enum(["true", "false"]).transform((v) => v === "true").optional(),
}).strict();
export type PickListQuery = z.infer<typeof PickListQuery>;

export const JobRun = z.object({
  id: Uuid,
  name: z.string(),
  status: JobRunStatus,
  startedAt: IsoDateTime.nullable(),
  finishedAt: IsoDateTime.nullable(),
  error: z.string().nullable(),
  createdAt: IsoDateTime,
});
export type JobRun = z.infer<typeof JobRun>;

export const JobListQuery = PageQuery.extend({
  name: z.string().trim().min(1).max(80).optional(),
  status: JobRunStatus.optional(),
}).strict();
export type JobListQuery = z.infer<typeof JobListQuery>;

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

export const ApiError = z.object({ error: z.string(), requestId: z.string(), issues: z.unknown().optional() });
export type ApiError = z.infer<typeof ApiError>;

export const PickVisibilityBody = z.object({ visibility: PickVisibility, version: z.number().int().positive() }).strict();
export type PickVisibilityBody = z.infer<typeof PickVisibilityBody>;

export const PickCloseBody = z
  .object({
    reason: PickClosedReason,
    outcomePct: DecimalString.optional(),
    version: z.number().int().positive(),
  })
  .strict();
export type PickCloseBody = z.infer<typeof PickCloseBody>;

/** Forward-only visibility ladder. Index = rank. */
export const VISIBILITY_ORDER = ["private", "internal", "public"] as const;
export function isForwardVisibility(from: PickVisibility, to: PickVisibility): boolean {
  return VISIBILITY_ORDER.indexOf(to) > VISIBILITY_ORDER.indexOf(from);
}

/** Legal next visibilities for a pick: only forward, none once public. */
export function legalNextVisibilities(from: PickVisibility): PickVisibility[] {
  return VISIBILITY_ORDER.filter((v) => isForwardVisibility(from, v));
}

// ---------------------------------------------------------------------------
// Pick create, update, detail
// ---------------------------------------------------------------------------

const PositiveDecimal = DecimalString.refine((v) => Number(v) > 0, "must be greater than zero");

// numeric(5,2) in the database; 25 is an input sanity cap, not a firm policy (see docs/KNOWN-GAPS.md).
export const MAX_RISK_PCT = 25;
export const RiskPctString = z
  .string()
  .regex(/^\d{1,2}(\.\d{1,2})?$/, "invalid percentage")
  .refine((v) => Number(v) > 0 && Number(v) <= MAX_RISK_PCT, `must be above 0 and at most ${MAX_RISK_PCT}`);

/**
 * Levels must be on the right side of entry for the direction:
 * long needs stop < entry < target, short needs target < entry < stop.
 * Returns an error message or null.
 */
export function checkPickLevels(
  direction: PickDirection,
  entry: string | number,
  target: string | number,
  stop: string | number,
): string | null {
  const e = Number(entry);
  const t = Number(target);
  const s = Number(stop);
  if (![e, t, s].every(Number.isFinite)) return "Levels must be numbers";
  if (direction === "long" && !(s < e && e < t)) return "For a long pick, stop must be below entry and target above entry";
  if (direction === "short" && !(t < e && e < s)) return "For a short pick, target must be below entry and stop above entry";
  return null;
}

export const PickCreateBody = z
  .object({
    instrumentId: Uuid,
    direction: PickDirection,
    entry: PositiveDecimal,
    target: PositiveDecimal,
    stop: PositiveDecimal,
    thesis: z.string().trim().min(1).max(5000),
    riskPerTradePct: RiskPctString,
  })
  .strict()
  .superRefine((v, ctx) => {
    const msg = checkPickLevels(v.direction, v.entry, v.target, v.stop);
    if (msg) ctx.addIssue({ code: "custom", message: msg, path: ["stop"] });
  });
export type PickCreateBody = z.infer<typeof PickCreateBody>;

export const PickUpdateBody = z
  .object({
    direction: PickDirection.optional(),
    entry: PositiveDecimal.optional(),
    target: PositiveDecimal.optional(),
    stop: PositiveDecimal.optional(),
    thesis: z.string().trim().min(1).max(5000).optional(),
    riskPerTradePct: RiskPctString.optional(),
    version: z.number().int().positive(),
  })
  .strict()
  .refine((v) => Object.keys(v).some((k) => k !== "version"), "Nothing to update");
export type PickUpdateBody = z.infer<typeof PickUpdateBody>;

// db_change: a change made outside the API (raw SQL), recorded by the database guard with no user.
export const PickEventType = z.enum(["created", "updated", "published", "closed", "visibility_changed", "db_change"]);
export type PickEventType = z.infer<typeof PickEventType>;

export const PickEvent = z.object({
  id: Uuid,
  type: PickEventType,
  actorId: Uuid.nullable(),
  actorName: z.string(),
  diff: z.record(z.string(), z.unknown()),
  createdAt: IsoDateTime,
});
export type PickEvent = z.infer<typeof PickEvent>;

export const PickDetail = Pick.extend({ events: z.array(PickEvent) });
export type PickDetail = z.infer<typeof PickDetail>;

// ---------------------------------------------------------------------------
// Watchlist writes
// ---------------------------------------------------------------------------

export const WatchlistCreateBody = z.object({ name: z.string().trim().min(1).max(80) }).strict();
export type WatchlistCreateBody = z.infer<typeof WatchlistCreateBody>;

export const WatchlistItemCreateBody = z
  .object({ instrumentId: Uuid, note: z.string().trim().max(500).optional() })
  .strict();
export type WatchlistItemCreateBody = z.infer<typeof WatchlistItemCreateBody>;

export const WatchlistItemParams = z.object({ id: Uuid, itemId: Uuid }).strict();
export type WatchlistItemParams = z.infer<typeof WatchlistItemParams>;

export const MAX_WATCHLISTS_PER_USER = 50;
export const MAX_WATCHLIST_ITEMS = 200;

// ---------------------------------------------------------------------------
// Market data reads: indicators, fundamentals, news
// ---------------------------------------------------------------------------

export const TimeframeQuery = z.object({ timeframe: Timeframe.default("1d") }).strict();
export type TimeframeQuery = z.infer<typeof TimeframeQuery>;

/**
 * IndicatorSnapshot.payload as the indicator service returns it (documented in docs/DATA-MODEL.md): one value per
 * bar sent, null while an indicator warms up. The service adds no timestamps; the indicators route adds `times`
 * (UTC unix seconds, oldest first) before the payload reaches the browser.
 */
const Series = z.array(z.number().nullable());
export const IndicatorPayload = z
  .object({
    times: z.array(z.number()),
    sma50: Series.optional(),
    sma200: Series.optional(),
    ema21: Series.optional(),
    rsi: Series.optional(),
    adx: z.object({ adx: Series }).passthrough().optional(),
    bbands: z.object({ upper: Series, middle: Series, lower: Series }).optional(),
    macd: z.object({ macd: Series, signal: Series, hist: Series }).optional(),
  })
  .passthrough();
export type IndicatorPayload = z.infer<typeof IndicatorPayload>;

export const IndicatorSnapshotDto = z.object({
  instrumentId: Uuid,
  timeframe: Timeframe,
  ts: IsoDateTime,
  engineVersion: z.string(),
  payload: z.record(z.string(), z.unknown()),
});
export type IndicatorSnapshotDto = z.infer<typeof IndicatorSnapshotDto>;

export const FundamentalDto = z.object({
  instrumentId: Uuid,
  periodType: z.enum(["annual", "quarterly", "ttm"]),
  periodEnd: z.string(),
  asOf: z.string(),
  source: z.string(),
  payload: z.record(z.string(), z.unknown()),
});
export type FundamentalDto = z.infer<typeof FundamentalDto>;

export const FundamentalsQuery = z.object({ periodType: z.enum(["annual", "quarterly", "ttm"]).default("ttm") }).strict();
export type FundamentalsQuery = z.infer<typeof FundamentalsQuery>;

export const NewsQuery = z.object({ take: z.coerce.number().int().min(1).max(50).default(20) }).strict();
export type NewsQuery = z.infer<typeof NewsQuery>;

export const NewsItemDto = z.object({
  id: Uuid,
  headline: z.string(),
  url: z.string(),
  publishedAt: IsoDateTime,
  source: z.string(),
});
export type NewsItemDto = z.infer<typeof NewsItemDto>;

// ---------------------------------------------------------------------------
// Screener
// ---------------------------------------------------------------------------

export const MAX_SCREENER_OFFSET = 10_000;

export const ScreenerBody = z
  .object({
    marketCapMin: z.number().nonnegative().max(1e15).optional(),
    marketCapMax: z.number().nonnegative().max(1e15).optional(),
    sector: z.string().trim().min(1).max(80).optional(),
    aboveSma200: z.boolean().optional(),
    rsiMin: z.number().min(0).max(100).optional(),
    rsiMax: z.number().min(0).max(100).optional(),
    adxMin: z.number().min(0).max(100).optional(),
    take: z.number().int().min(1).max(MAX_PAGE_SIZE).default(50),
    offset: z.number().int().min(0).max(MAX_SCREENER_OFFSET).default(0),
  })
  .strict()
  .superRefine((v, ctx) => {
    if (v.marketCapMin !== undefined && v.marketCapMax !== undefined && v.marketCapMin > v.marketCapMax) {
      ctx.addIssue({ code: "custom", message: "marketCapMin must not exceed marketCapMax", path: ["marketCapMin"] });
    }
    if (v.rsiMin !== undefined && v.rsiMax !== undefined && v.rsiMin > v.rsiMax) {
      ctx.addIssue({ code: "custom", message: "rsiMin must not exceed rsiMax", path: ["rsiMin"] });
    }
  });
export type ScreenerBody = z.infer<typeof ScreenerBody>;
export type ScreenerInput = z.input<typeof ScreenerBody>;

export const ScreenerRow = z.object({
  instrumentId: Uuid,
  ticker: z.string(),
  name: z.string(),
  sector: z.string().nullable(),
  marketCap: z.number().nullable(),
  close: DecimalString.nullable(),
  sma200: z.number().nullable(),
  rsi: z.number().nullable(),
  adx: z.number().nullable(),
});
export type ScreenerRow = z.infer<typeof ScreenerRow>;
